import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowsClockwise, CloudArrowUp, Broom, Globe, CircleNotch, CheckCircle, FolderOpen, File as FileIcon, UploadSimple, HardDrives } from '@phosphor-icons/react';

// Recursively read a directory entry, preserving nested paths (basePath prefix).
const traverse = async (entry, basePath, out) => {
  if (entry.isFile) {
    const f = await new Promise((res) => entry.file(res));
    out.push({ file: f, path: basePath + entry.name });
  } else if (entry.isDirectory) {
    const reader = entry.createReader();
    let all = [];
    while (true) {
      const batch = await new Promise((res) => reader.readEntries(res, () => res([])));
      if (!batch.length) break;
      all = all.concat(batch);
    }
    for (const child of all) await traverse(child, basePath + entry.name + '/', out);
  }
};

// Extract files from a drop event. Dropped folders have their CONTENTS rooted (folder name stripped).
const getFilesFromDrop = async (dataTransfer) => {
  const out = [];
  const roots = [];
  if (dataTransfer.items && dataTransfer.items.length) {
    for (const it of dataTransfer.items) {
      const entry = it.webkitGetAsEntry && it.webkitGetAsEntry();
      if (entry) roots.push(entry);
    }
  }
  if (roots.length) {
    for (const entry of roots) {
      if (entry.isFile) {
        await traverse(entry, '', out);
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        let all = [];
        while (true) {
          const batch = await new Promise((res) => reader.readEntries(res, () => res([])));
          if (!batch.length) break;
          all = all.concat(batch);
        }
        for (const child of all) await traverse(child, '', out); // root the folder's contents
      }
    }
  } else {
    for (const f of dataTransfer.files) out.push({ file: f, path: f.name });
  }
  return out;
};

const AzureFileUploader = () => {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [sites, setSites] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [synced, setSynced] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [lastResult, setLastResult] = useState(null);
  const [purging, setPurging] = useState(false);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  useEffect(() => {
    axios.get('/api/azure/accounts', { withCredentials: true })
      .then((r) => setAccounts(r.data))
      .catch(() => toast.error('Failed to load Azure accounts'));
  }, []);

  const syncSites = async () => {
    if (!accountId) { toast.error('Select an Azure account first'); return; }
    setSyncing(true); setSites([]); setSelectedSite(null); setSynced(false);
    try {
      const r = await axios.get(`/api/azure/accounts/${accountId}/sites`, { withCredentials: true });
      setSites(r.data);
      setSynced(true);
      toast.success(`Synced ${r.data.length} static site${r.data.length === 1 ? '' : 's'} from Azure`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const doUpload = async (items) => {
    if (!selectedSite) { toast.error('Select a site to upload to'); return; }
    if (!items.length) { toast.error('No files found'); return; }
    setUploading(true); setProgress(0); setLastResult(null);
    try {
      const fd = new FormData();
      items.forEach(({ file, path }) => { fd.append('files', file); fd.append('paths', path); });
      const r = await axios.post(
        `/api/azure/accounts/${accountId}/sites/${selectedSite.name}/upload`,
        fd,
        {
          withCredentials: true,
          onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
        }
      );
      setLastResult(r.data);
      toast.success(`Uploaded ${r.data.uploaded} file(s) to ${selectedSite.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = async (e) => {
    e.preventDefault(); setDragOver(false);
    if (!selectedSite) { toast.error('Select a site first'); return; }
    const items = await getFilesFromDrop(e.dataTransfer);
    doUpload(items);
  };

  const onPickFiles = (e) => {
    const items = Array.from(e.target.files).map((f) => ({ file: f, path: f.name }));
    doUpload(items);
    e.target.value = '';
  };

  const onPickFolder = (e) => {
    const items = Array.from(e.target.files).map((f) => ({
      file: f,
      path: f.webkitRelativePath ? f.webkitRelativePath.split('/').slice(1).join('/') || f.name : f.name,
    }));
    doUpload(items);
    e.target.value = '';
  };

  const purgeSite = async () => {
    if (!selectedSite) return;
    setPurging(true);
    try {
      await axios.post(`/api/azure/accounts/${accountId}/sites/${selectedSite.name}/purge`, {}, { withCredentials: true });
      toast.success(`CDN cache purged for ${selectedSite.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500" data-testid="azure-uploader">
      <div>
        <h1 className="text-4xl font-black text-[#0078D4] mb-2">FILE UPLOADER</h1>
        <p className="text-slate-400 text-sm max-w-2xl">
          Sync your deployed static sites from Azure, pick one, then drag &amp; drop files or a folder to push them
          straight into its <span className="font-mono text-[#4db8ff]">$web</span> container. Folders keep their structure.
        </p>
      </div>

      {/* Account + Sync */}
      <div className="bg-card border border-[#0078D4]/20 p-6 space-y-4">
        <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">1. Azure Account</label>
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            data-testid="uploader-account-select"
            value={accountId}
            onChange={(e) => { setAccountId(e.target.value); setSites([]); setSelectedSite(null); setSynced(false); }}
            className="flex-1 bg-[#0A0B0E] border border-white/10 p-3 focus:outline-none focus:border-[#0078D4] text-white"
          >
            <option value="">-- Choose an account --</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
          <button
            data-testid="uploader-sync-btn"
            onClick={syncSites}
            disabled={!accountId || syncing}
            className="flex items-center justify-center gap-2 bg-[#0078D4] text-white px-6 py-3 font-bold uppercase tracking-wider hover:bg-[#005a9e] transition-colors disabled:opacity-50"
          >
            {syncing ? <CircleNotch size={18} className="animate-spin" /> : <ArrowsClockwise size={18} weight="bold" />}
            {syncing ? 'Syncing…' : 'Sync Sites'}
          </button>
        </div>
      </div>

      {/* Sites list */}
      {synced && (
        <div className="bg-card border border-[#0078D4]/20 p-6 space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">2. Select a Site ({sites.length})</label>
          </div>
          {sites.length === 0 ? (
            <div className="text-slate-500 font-mono text-sm py-4">No static-website storage accounts found on this subscription.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[360px] overflow-y-auto" data-testid="uploader-site-list">
              {sites.map((s) => {
                const active = selectedSite?.name === s.name;
                return (
                  <button
                    key={s.name}
                    data-testid={`uploader-site-${s.name}`}
                    onClick={() => setSelectedSite(s)}
                    className={`text-left p-3 border transition-colors ${active ? 'border-[#0078D4] bg-[#0078D4]/10' : 'border-white/10 bg-[#0A0B0E] hover:border-white/30'}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-3.5 h-3.5 border flex items-center justify-center text-[9px] shrink-0 ${active ? 'bg-[#0078D4] border-[#0078D4] text-white' : 'border-white/30'}`}>{active ? '✓' : ''}</span>
                      <HardDrives size={15} className="text-slate-500 shrink-0" />
                      <span className="text-sm text-white font-bold truncate">{s.name}</span>
                    </div>
                    {s.fd_url && (
                      <a href={s.fd_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="ml-5 text-[11px] font-mono text-[#4db8ff] hover:underline flex items-center gap-1 truncate">
                        <Globe size={11} /> {s.fd_url.replace('https://', '')}
                      </a>
                    )}
                    {!s.fd_url && s.web_url && (
                      <span className="ml-5 text-[11px] font-mono text-slate-500 truncate block">{s.web_url.replace('https://', '')}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Upload zone */}
      {selectedSite && (
        <div className="bg-card border border-[#0078D4]/20 p-6 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold">3. Upload to <span className="text-white font-mono normal-case">{selectedSite.name}</span></label>
            <button
              data-testid="uploader-purge-btn"
              onClick={purgeSite}
              disabled={purging}
              className="flex items-center gap-2 border border-amber-500/50 text-amber-400 px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-amber-500/10 transition-colors disabled:opacity-50"
              title="Purge Front Door CDN cache for this site"
            >
              {purging ? <CircleNotch size={14} className="animate-spin" /> : <Broom size={14} weight="bold" />}
              {purging ? 'Purging…' : 'Purge CDN Cache'}
            </button>
          </div>

          <div
            data-testid="uploader-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`border-2 border-dashed p-10 text-center transition-colors ${dragOver ? 'border-[#0078D4] bg-[#0078D4]/10' : 'border-white/15 bg-[#0A0B0E]'} ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
          >
            {uploading ? (
              <div className="space-y-3">
                <CircleNotch size={40} className="animate-spin text-[#0078D4] mx-auto" />
                <p className="text-slate-300 font-mono text-sm">Uploading… {progress}%</p>
                <div className="w-full max-w-sm mx-auto h-2 bg-white/10 overflow-hidden">
                  <div className="h-full bg-[#0078D4] transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            ) : (
              <>
                <CloudArrowUp size={48} className="text-[#0078D4] mx-auto mb-3" weight="thin" />
                <p className="text-white font-bold">Drag &amp; drop files or a folder here</p>
                <p className="text-slate-500 text-xs mt-1 font-mono">Uploads into the site's $web container · overwrites existing files</p>
                <div className="flex items-center justify-center gap-3 mt-5">
                  <button onClick={() => fileInputRef.current?.click()} data-testid="uploader-pick-files" className="flex items-center gap-2 border border-white/20 text-slate-200 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:border-[#0078D4] transition-colors">
                    <FileIcon size={15} /> Choose Files
                  </button>
                  <button onClick={() => folderInputRef.current?.click()} data-testid="uploader-pick-folder" className="flex items-center gap-2 border border-white/20 text-slate-200 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:border-[#0078D4] transition-colors">
                    <FolderOpen size={15} /> Choose Folder
                  </button>
                </div>
                <input ref={fileInputRef} type="file" multiple hidden onChange={onPickFiles} />
                <input ref={folderInputRef} type="file" hidden webkitdirectory="" directory="" onChange={onPickFolder} />
              </>
            )}
          </div>

          {lastResult && (
            <div className="border border-[#39FF14]/30 bg-[#39FF14]/5 p-4" data-testid="uploader-result">
              <div className="flex items-center gap-2 text-[#39FF14] font-bold text-sm mb-2">
                <CheckCircle size={16} weight="fill" /> Uploaded {lastResult.uploaded} file(s)
              </div>
              <div className="max-h-40 overflow-y-auto text-[11px] font-mono text-slate-400 space-y-0.5">
                {(lastResult.files || []).map((f) => (
                  <div key={f} className="flex items-center gap-1"><UploadSimple size={11} className="text-[#39FF14]" /> {f}</div>
                ))}
              </div>
              <p className="text-slate-500 text-[11px] mt-2">Tip: Front Door usually reflects storage changes fast, but hit <strong className="text-amber-400">Purge CDN Cache</strong> to force it instantly.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AzureFileUploader;
